import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, Loader2, Bot, User as UserIcon, FileText, ChevronRight, ChevronDown, Zap } from 'lucide-react';
import { copyText } from '../utils/clipboard';
import { PlacesCardSurface } from './PlaceComponents';
import { RunReportSurface } from './RunReportComponents';
import { CATALOG_ID_ANALYSIS, type SurfaceActionPayload } from '../utils/runReport';

// ── Shared interfaces ──

export interface ChatThought {
  seq: number;
  text: string;
  elapsed_ms: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  source?: 'active' | 'archive';
  files?: { name: string; media_type: string; preview?: string }[];
  toolUi?: A2UISurface;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- message metadata is dynamic
  metadata?: Record<string, any> | null;
  /** Gemini thinking chunks. Present when fetched with `?include=thoughts`. */
  thoughts?: ChatThought[];
}

export interface ThreadStats {
  totalTurns: number;
  activeTurns: number;
  activeTokens: number;
  idleHours: number;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- A2UI components are schema-driven dynamic data */
export interface A2UIComponent {
  id: string;
  component: string;
  [key: string]: any;
}
export interface A2UISurface {
  version?: string;
  // v1.0 wire envelope (current)
  createSurface?: {
    surfaceId: string;
    catalogId?: string;
    components?: A2UIComponent[];
    surfaceProperties?: Record<string, any>;
    // v1.0 card surfaces seed FormCard inputs from this pre-filled model.
    dataModel?: { form?: Record<string, any>; [key: string]: any };
  };
  // legacy v0.9 fields — surfaces persisted before the envelope migration
  surface_id?: string;
  components?: A2UIComponent[];
  theme?: Record<string, any>;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// Extract the component array from a surface regardless of shape — v1.0 wire
// (`createSurface.components`) or legacy v0.9 (`components`). Mirrors the backend
// `surface_components()` helper so both render paths (live SSE tool_result and
// persisted tool_ui) stay shape-agnostic. Returns null if neither shape is present.
export const surfaceComponents = (
  surface: A2UISurface | null | undefined,
): A2UIComponent[] | null => {
  const fromEnvelope = surface?.createSurface?.components;
  if (Array.isArray(fromEnvelope)) return fromEnvelope;
  if (Array.isArray(surface?.components)) return surface.components;
  return null;
};

// The semantic card components. When the single root component
// is one of these, the renderer dispatches to a dedicated card renderer instead
// of the legacy v0.9 primitive tree. Any other root falls through to the
// primitive path (persisted pre-migration surfaces + forward-compat).
// (PlacesCard dispatches earlier, to its own map surface.)
const CARD_TYPES = new Set([
  'FeatureRecommendCard',
  'EventConfirmCard',
  'FormCard',
  'AskUserQuestionCard',
]);

// EventConfirmCard day-by-day view is derived on the FE by grouping `events` on
// the date portion of each event's `start_time` (RFC3339) — the agent no longer
// authors a `days` block. Events without a `start_time` collect under an empty
// key so the renderer can list them plainly last. Returns date-keyed buckets
// plus a sorted (ascending) list of the distinct dates present.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic event
const groupEventsByDate = (events: any[]): { dates: string[]; byDate: Record<string, any[]> } => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic event
  const byDate: Record<string, any[]> = {};
  events.forEach((ev) => {
    const date = typeof ev?.start_time === 'string' ? ev.start_time.slice(0, 10) : '';
    (byDate[date] ||= []).push(ev);
  });
  const dates = Object.keys(byDate)
    .filter((d) => d !== '')
    .sort();
  return { dates, byDate };
};

// Seed value for the FormCard local form state: the surface's pre-filled
// `dataModel.form` ({key: value}) re-keyed by each field's `path` (/form/{key}).
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic form data
export const seedFormDataModel = (surface: A2UISurface | null | undefined): Record<string, any> => {
  const form = surface?.createSurface?.dataModel?.form;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic form data
  const seeded: Record<string, any> = {};
  if (form && typeof form === 'object') {
    for (const [k, v] of Object.entries(form)) {
      seeded[`/form/${k}`] = v;
    }
  }
  return seeded;
};

// Fix malformed markdown from AI and convert bold to HTML for CJK compatibility
export const preprocessMarkdown = (text: string): string => {
  let result = text;
  result = result.replace(/(?<!~)~(?!~)/g, '&#126;');
  result = result.replace(/\*\*\s*([^*]+?)\s*\*\*/g, (_, content) => {
    const trimmed = content.trim();
    return trimmed ? `<strong>${trimmed}</strong>` : '';
  });
  return result;
};

// Markdown component for rendering chat messages
export const MarkdownContent: React.FC<{ content: string; isUser?: boolean }> = React.memo(({ content, isUser }) => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const processedContent = preprocessMarkdown(content);

  const copyToClipboard = (code: string) => {
    copyText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        code({ className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const codeString = String(children).replace(/\n$/, '');

          if (match) {
            return (
              <div className="relative group my-2">
                <div className="flex items-center justify-between bg-slate-800 text-slate-300 text-xs px-3 py-1 rounded-t-lg">
                  <span>{match[1]}</span>
                  <button
                    onClick={() => copyToClipboard(codeString)}
                    className="flex items-center gap-1 hover:text-white transition-colors"
                  >
                    {copiedCode === codeString ? (
                      <>
                        <Check size={12} /> 복사됨
                      </>
                    ) : (
                      <>
                        <Copy size={12} /> 복사
                      </>
                    )}
                  </button>
                </div>
                <SyntaxHighlighter
                  style={oneDark}
                  language={match[1]}
                  PreTag="div"
                  customStyle={{
                    margin: 0,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    fontSize: '13px',
                  }}
                >
                  {codeString}
                </SyntaxHighlighter>
              </div>
            );
          }

          return (
            <code
              className={`${isUser ? 'bg-blue-700/50' : 'bg-slate-100'} px-1.5 py-0.5 rounded text-sm font-mono`}
              {...props}
            >
              {children}
            </code>
          );
        },
        p({ children }) {
          return <p className="mb-2 last:mb-0">{children}</p>;
        },
        ul({ children }) {
          return <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>;
        },
        ol({ children }) {
          return <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>;
        },
        li({ children }) {
          return <li>{children}</li>;
        },
        h1({ children }) {
          return <h1 className="text-xl font-bold mb-2 mt-3">{children}</h1>;
        },
        h2({ children }) {
          return <h2 className="text-lg font-bold mb-2 mt-3">{children}</h2>;
        },
        h3({ children }) {
          return <h3 className="text-base font-bold mb-1 mt-2">{children}</h3>;
        },
        blockquote({ children }) {
          return (
            <blockquote className={`border-l-4 ${isUser ? 'border-blue-300' : 'border-slate-300'} pl-3 my-2 italic`}>
              {children}
            </blockquote>
          );
        },
        a({ href, children }) {
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${isUser ? 'text-blue-200 hover:text-white' : 'text-blue-600 hover:text-blue-800'} underline`}
            >
              {children}
            </a>
          );
        },
        table({ children }) {
          return (
            <div className="overflow-x-auto my-2">
              <table className="min-w-full border-collapse border border-slate-300">{children}</table>
            </div>
          );
        },
        th({ children }) {
          return <th className="border border-slate-300 px-3 py-1 bg-slate-100 font-semibold text-left">{children}</th>;
        },
        td({ children }) {
          return <td className="border border-slate-300 px-3 py-1">{children}</td>;
        },
        hr() {
          return <hr className="my-3 border-slate-300" />;
        },
        strong({ children }) {
          return <strong className="font-bold">{children}</strong>;
        },
        em({ children }) {
          return <em className="italic">{children}</em>;
        },
      }}
    >
      {processedContent}
    </ReactMarkdown>
  );
});
MarkdownContent.displayName = 'MarkdownContent';

// A2UI Surface Renderer — renders a2ui tool results
// Design doc: latest surface is editable, previous ones are read-only.
// Button action.event → collect path-based data model → auto-send as chat message.
export const A2UISurfaceRenderer: React.FC<{
  surface: A2UISurface;
  isInteractive: boolean;
  onSubmit?: (message: string) => void;
  /** Run-report taps (list rows, chips) go to the server as `surface_action`, not text. */
  onSurfaceAction?: (action: SurfaceActionPayload, label: string) => void;
}> = React.memo(({ surface, isInteractive, onSubmit, onSurfaceAction }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic form data
  const [dataModel, setDataModel] = useState<Record<string, any>>(() => seedFormDataModel(surface));
  const [submitted, setSubmitted] = useState(false);
  // AskUserQuestionCard answers, keyed by question index: the picked option
  // labels (multiSelect keeps several) plus the free-text "Other" the client
  // always offers — the card schema never carries an Other option.
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [otherText, setOtherText] = useState<Record<number, string>>({});
  const disabled = !isInteractive || submitted;

  // Defensive guard: a2ui tool errors (e.g. sequencing-gate rejections) may be
  // persisted as tool_ui as a bare {"error": ...} object with no components.
  // Render nothing rather than crash the whole thread view.
  const components = surfaceComponents(surface);
  if (!components) {
    return null;
  }

  const compMap = new Map(components.map((c) => [c.id, c]));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic form values
  const setValue = (path: string, value: any) => {
    if (disabled) return;
    setDataModel((prev) => ({ ...prev, [path]: value }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI action definition
  const handleAction = (actionDef: any) => {
    if (disabled) return;

    // A2UI buttons carry one of two action families, discriminated by the
    // event's `name` (the backend ActionEvent field — {name, context?}):
    //   1. `navigate:{feature}` — dashboard is read-only, so this is a demo
    //      surface; log it and mark submitted to disable the row, but do NOT
    //      call onSubmit (would inject a user message into the thread).
    //   2. everything else (approve/change/submit*) — treat as form submission:
    //      collect the data model into a human-readable summary and send via
    //      onSubmit.
    //
    // Accept a bare string too, for robustness against callers passing the name
    // directly rather than the event object.
    const actionStr =
      typeof actionDef === 'string'
        ? actionDef
        : typeof actionDef?.name === 'string'
        ? actionDef.name
        : '';
    if (actionStr.startsWith('navigate:')) {
      // eslint-disable-next-line no-console
      console.info('[A2UI] navigate action (dashboard no-op):', actionDef);
      setSubmitted(true);
      return;
    }

    if (!onSubmit) return;
    setSubmitted(true);
    // Build human-readable summary from data model
    const lines: string[] = [];
    for (const [path, value] of Object.entries(dataModel)) {
      // Find component label by path
      const comp = components.find((c) => c.path === path);
      const label = comp?.label || path.split('/').pop() || path;
      const displayVal = Array.isArray(value) ? value.join(', ') : String(value);
      lines.push(`${label}: ${displayVal}`);
    }
    const summary = lines.join(', ') || '(제출)';
    onSubmit(summary);
  };

  const renderComponent = (comp: A2UIComponent): React.ReactNode => {
    switch (comp.component) {
      case 'Text': {
        const isHeading = comp.variant?.startsWith('h');
        const level = isHeading ? parseInt(comp.variant!.replace('h', '')) : 0;
        const sizeClass =
          level <= 2 ? 'text-base font-bold' : level <= 4 ? 'text-sm font-semibold' : 'text-sm font-medium';
        return (
          <div key={comp.id} className={isHeading ? sizeClass + ' text-slate-800' : 'text-sm text-slate-700'}>
            {comp.text || ''}
          </div>
        );
      }
      case 'Button':
        return (
          <button
            key={comp.id}
            disabled={disabled}
            onClick={() => comp.action?.event && handleAction(comp.action.event)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              comp.variant === 'primary'
                ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:bg-slate-50 disabled:text-slate-400'
            } disabled:cursor-not-allowed`}
          >
            {comp.text || 'Button'}
          </button>
        );
      case 'TextField':
        return (
          <div key={comp.id} className="space-y-1">
            {comp.label && <label className="text-xs font-medium text-slate-600">{comp.label}</label>}
            <input
              type="text"
              placeholder={comp.placeholder || ''}
              value={comp.path ? (dataModel[comp.path] ?? '') : ''}
              onChange={(e) => comp.path && setValue(comp.path, e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>
        );
      case 'ChoicePicker': {
        const selected = comp.path ? dataModel[comp.path] : undefined;
        const isMulti = comp.mode === 'multi';
        return (
          <div key={comp.id} className="space-y-1">
            {comp.label && <label className="text-xs font-medium text-slate-600">{comp.label}</label>}
            <div className="flex flex-wrap gap-2">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic option */}
              {(comp.options || []).map((opt: any, i: number) => {
                const optValue = typeof opt === 'string' ? opt : opt.value || opt.label;
                const optLabel = typeof opt === 'string' ? opt : opt.label || opt.value;
                const isSelected = isMulti
                  ? Array.isArray(selected) && selected.includes(optValue)
                  : selected === optValue;
                return (
                  <button
                    key={i}
                    disabled={disabled}
                    onClick={() => {
                      if (!comp.path || disabled) return;
                      if (isMulti) {
                        const cur = Array.isArray(selected) ? selected : [];
                        setValue(
                          comp.path,
                          cur.includes(optValue) ? cur.filter((v: string) => v !== optValue) : [...cur, optValue],
                        );
                      } else {
                        setValue(comp.path, optValue);
                      }
                    }}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      isSelected
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400 hover:text-blue-600'
                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {optLabel}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }
      case 'Slider': {
        const val = comp.path ? (dataModel[comp.path] ?? comp.value ?? comp.min ?? 0) : (comp.value ?? comp.min ?? 0);
        return (
          <div key={comp.id} className="space-y-1">
            {comp.label && (
              <label className="text-xs font-medium text-slate-600">
                {comp.label} {comp.path && <span className="text-blue-600 ml-1">{val}</span>}
              </label>
            )}
            <input
              type="range"
              min={comp.min || 0}
              max={comp.max || 100}
              step={comp.step || 1}
              value={val}
              onChange={(e) => comp.path && setValue(comp.path, Number(e.target.value))}
              disabled={disabled}
              className="w-full accent-blue-600"
            />
          </div>
        );
      }
      case 'CheckBox': {
        const checked = comp.path ? !!dataModel[comp.path] : false;
        return (
          <label
            key={comp.id}
            className={`flex items-center gap-2 text-sm text-slate-700 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => comp.path && setValue(comp.path, e.target.checked)}
              disabled={disabled}
              className="rounded accent-blue-600"
            />
            {comp.label || ''}
          </label>
        );
      }
      case 'DateTimeInput': {
        const inputType = comp.mode === 'time' ? 'time' : comp.mode === 'datetime' ? 'datetime-local' : 'date';
        return (
          <div key={comp.id} className="space-y-1">
            {comp.label && <label className="text-xs font-medium text-slate-600">{comp.label}</label>}
            <input
              type={inputType}
              value={comp.path ? (dataModel[comp.path] ?? '') : ''}
              onChange={(e) => comp.path && setValue(comp.path, e.target.value)}
              disabled={disabled}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
            />
          </div>
        );
      }
      case 'Divider':
        return <hr key={comp.id} className="border-slate-200" />;
      case 'Image':
        return comp.src ? (
          <img key={comp.id} src={comp.src} alt={comp.alt || ''} className="max-w-full rounded" />
        ) : null;
      case 'Card': {
        const childComp = comp.child ? compMap.get(comp.child) : null;
        return (
          <div key={comp.id} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {childComp && renderComponent(childComp)}
          </div>
        );
      }
      case 'Column': {
        const children = (comp.children || []).map((cid: string) => compMap.get(cid)).filter(Boolean);
        return (
          <div key={comp.id} className="p-4 space-y-3">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic component */}
            {children.map((c: any) => renderComponent(c))}
          </div>
        );
      }
      case 'Row': {
        const children = (comp.children || []).map((cid: string) => compMap.get(cid)).filter(Boolean);
        return (
          <div key={comp.id} className="flex gap-3 items-center">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic component */}
            {children.map((c: any) => renderComponent(c))}
          </div>
        );
      }
      default:
        return (
          <div key={comp.id} className="text-xs text-slate-400">
            [{comp.component}]
          </div>
        );
    }
  };

  // ── Card renderers (v1.0 semantic cards) ────────────────────────────────
  // The primitive tree is collapsed into a single semantic card. Each card is
  // self-contained (own buttons), so these bypass renderComponent. Shared button
  // styling mirrors the primitive `Button` case above.
  const cardButton = (
    label: string,
    variant: 'primary' | 'secondary',
    onClick: () => void,
    key: string,
  ) => (
    <button
      key={key}
      disabled={disabled}
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        variant === 'primary'
          ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:bg-slate-50 disabled:text-slate-400'
      } disabled:cursor-not-allowed`}
    >
      {label}
    </button>
  );

  const cardHeader = (title?: string, subtitle?: string) => (
    <div className="space-y-1">
      {title && <div className="text-base font-bold text-slate-800">{title}</div>}
      {subtitle && <div className="text-sm text-slate-600">{subtitle}</div>}
    </div>
  );

  // Human-readable summary → user_message. Used by calendar approve buttons; the
  // dashboard is a read-only demo so it does not call the BE calendar API.
  const submitSummary = (text: string) => {
    if (!onSubmit) return;
    setSubmitted(true);
    onSubmit(text);
  };

  // change button — no BE round-trip in the demo, just disable the row.
  const changeCard = () => {
    // eslint-disable-next-line no-console
    console.info('[A2UI] change action (dashboard no-op)');
    setSubmitted(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic card
  const approveCalendar = (card: any) => {
    // events are the sole source — a multi-day plan is summarized by its events
    // (no separate days/period; the day view is derived at render time).
    const lines: string[] = [`[${card.title || '일정'} 승인]`];
    for (const ev of card.events || []) {
      lines.push(`- ${ev.summary}${ev.start_time ? ` (${ev.start_time})` : ''}`);
    }
    submitSummary(lines.join('\n'));
  };

  // FormCard submit → serialize collected values per contract.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic card
  // FormCard 제출 형식 계약: FormCard 는 구조화 RPC 가 아니라 이 텍스트를
  // user_message 로 보낸다. **이 직렬화가 계약의 authoritative producer** — 형식은
  //   [{title} 응답]
  //   - {label}: {value}   (choice=옵션 라벨 · slider={value}{unit} · date=ISO · checkbox=예/아니오)
  // 이며, a2ui 프롬프트(§3 FormCard)의 제출-형식 서술이 이와 정확히 일치해야 한다.
  // 둘 중 하나만 바뀌면 agent 가 폼 응답을 오해석하므로 함께 고칠 것.
  const serializeForm = (card: any): string => {
    const lines: string[] = [`[${card.title || '응답'} 응답]`];
    for (const field of card.fields || []) {
      const path = field.path || `/form/${field.key}`;
      const raw = dataModel[path];
      let display = '';
      switch (field.type) {
        case 'choice': {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic option
          const toLabel = (v: any) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic option
            const o = (field.options || []).find((o: any) => (o.value ?? o.label) === v);
            return o ? o.label ?? o.value : String(v);
          };
          if (Array.isArray(raw)) display = raw.map(toLabel).join(', ');
          else if (raw !== undefined && raw !== null && raw !== '') display = toLabel(raw);
          break;
        }
        case 'slider':
          display = raw !== undefined && raw !== null ? `${raw}${field.unit || ''}` : '';
          break;
        case 'checkbox':
          display = raw ? '예' : '아니오';
          break;
        case 'date':
          // date input value is already ISO-8601 (YYYY-MM-DD)
          display = raw !== undefined && raw !== null ? String(raw) : '';
          break;
        default:
          display = raw !== undefined && raw !== null ? String(raw) : '';
      }
      lines.push(`- ${field.label}: ${display}`);
    }
    return lines.join('\n');
  };

  // AskUserQuestionCard submit → serialize the picked answers per contract.
  // Mirrors `serializeForm` above: this is the **authoritative producer** of the
  // reply text, and the a2ui prompt (§5 AskUserQuestionCard) must describe the
  // same shape —
  //   [질문 응답]
  //   - {header}: {label}        (multiSelect → labels joined by ", ")
  // `header` is the key (not the question text) because it is short, required,
  // and unique within a card, and the agent still has the questions it asked in
  // its own tool call. Unanswered questions are omitted, mirroring the upstream
  // tools' behavior. Change this and the prompt together or the agent
  // misreads the reply.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic card
  const serializeQuestions = (card: any): string => {
    const lines: string[] = ['[질문 응답]'];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic question
    (card.questions || []).forEach((q: any, i: number) => {
      const picked = [...(answers[i] || [])];
      const other = (otherText[i] || '').trim();
      if (other) picked.push(other);
      if (picked.length) lines.push(`- ${q.header}: ${picked.join(', ')}`);
    });
    return lines.join('\n');
  };

  // A question counts as answered once it has a picked option or free text.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic card
  const allQuestionsAnswered = (card: any): boolean =>
    (card.questions || []).every(
      (_q: unknown, i: number) =>
        (answers[i] || []).length > 0 || (otherText[i] || '').trim() !== '',
    );

  const toggleAnswer = (qIdx: number, label: string, multi: boolean) => {
    setAnswers((prev) => {
      const cur = prev[qIdx] || [];
      if (!multi) return { ...prev, [qIdx]: cur[0] === label ? [] : [label] };
      return {
        ...prev,
        [qIdx]: cur.includes(label) ? cur.filter((l) => l !== label) : [...cur, label],
      };
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic field
  const renderFormField = (field: any, idx: number): React.ReactNode => {
    const path = field.path || `/form/${field.key}`;
    const key = field.key || `field_${idx}`;
    const val = dataModel[path];
    const requiredMark = field.required ? <span className="text-red-500 ml-0.5">*</span> : null;

    switch (field.type) {
      case 'choice': {
        const isMulti = field.mode === 'multi';
        const selected = val;
        return (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              {field.label}
              {requiredMark}
            </label>
            <div className="flex flex-wrap gap-2">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic option */}
              {(field.options || []).map((opt: any, i: number) => {
                const optValue = opt.value ?? opt.label;
                const optLabel = opt.label ?? opt.value;
                const isSelected = isMulti
                  ? Array.isArray(selected) && selected.includes(optValue)
                  : selected === optValue;
                return (
                  <button
                    key={i}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      if (isMulti) {
                        const cur = Array.isArray(selected) ? selected : [];
                        setValue(
                          path,
                          cur.includes(optValue)
                            ? cur.filter((v: string) => v !== optValue)
                            : [...cur, optValue],
                        );
                      } else {
                        setValue(path, optValue);
                      }
                    }}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      isSelected
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400 hover:text-blue-600'
                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {optLabel}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }
      case 'slider': {
        const cur = val ?? field.default ?? field.min ?? 0;
        return (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              {field.label} <span className="text-blue-600 ml-1">{cur}{field.unit || ''}</span>
              {requiredMark}
            </label>
            <input
              type="range"
              min={field.min ?? 0}
              max={field.max ?? 100}
              step={field.step ?? 1}
              value={cur}
              onChange={(e) => setValue(path, Number(e.target.value))}
              disabled={disabled}
              className="w-full accent-blue-600"
            />
          </div>
        );
      }
      case 'checkbox':
        return (
          <label
            key={key}
            className={`flex items-center gap-2 text-sm text-slate-700 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              checked={!!val}
              onChange={(e) => setValue(path, e.target.checked)}
              disabled={disabled}
              className="rounded accent-blue-600"
            />
            {field.label}
            {requiredMark}
          </label>
        );
      case 'date':
        return (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              {field.label}
              {requiredMark}
            </label>
            <input
              type="date"
              value={val ?? ''}
              onChange={(e) => setValue(path, e.target.value)}
              disabled={disabled}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
            />
          </div>
        );
      default:
        return (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              {field.label}
              {requiredMark}
            </label>
            {field.multiline ? (
              <textarea
                placeholder={field.placeholder || ''}
                value={val ?? ''}
                onChange={(e) => setValue(path, e.target.value)}
                disabled={disabled}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
              />
            ) : (
              <input
                type="text"
                placeholder={field.placeholder || ''}
                value={val ?? ''}
                onChange={(e) => setValue(path, e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
              />
            )}
          </div>
        );
    }
  };

  const cardWrap = 'rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden';
  const cardBody = 'p-4 space-y-3';

  const renderCard = (card: A2UIComponent): React.ReactNode => {
    switch (card.component) {
      case 'FeatureRecommendCard':
        return (
          <div className={cardWrap}>
            <div className={cardBody}>
              {cardHeader(card.title, card.subtitle)}
              {/* navigate is a built-in app feature jump — read-only demo no-op */}
              {cardButton(
                card.cta_label || '시작',
                'primary',
                () => handleAction({ name: `navigate:${card.feature}` }),
                'cta',
              )}
            </div>
          </div>
        );
      case 'EventConfirmCard': {
        // Day-by-day view is derived on the FE by grouping `events` on the date
        // portion of each `start_time` — the agent no longer authors `days`.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic event
        const events: any[] = Array.isArray(card.events) ? card.events : [];
        const { dates, byDate } = groupEventsByDate(events);
        const undated = byDate[''] || [];
        // A single distinct date (or ≤1 event) → plain flat list; multiple
        // distinct dates → one section per date (ascending) + undated last.
        const multiDay = dates.length > 1;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic event
        const eventLine = (ev: any, key: number) => (
          <li key={key} className="flex items-start gap-2">
            <span className="text-slate-400">•</span>
            <span>
              {ev.summary}
              {ev.start_time ? ` — ${ev.start_time}` : ''}
            </span>
          </li>
        );
        return (
          <div className={cardWrap}>
            <div className={cardBody}>
              {cardHeader(card.title, card.subtitle)}
              {multiDay ? (
                <div className="space-y-2">
                  {dates.map((date) => (
                    <div key={date} className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                      <div className="text-xs font-semibold text-slate-700">{date}</div>
                      <ul className="mt-1 space-y-1 text-sm text-slate-600">
                        {byDate[date].map((ev, i) => eventLine(ev, i))}
                      </ul>
                    </div>
                  ))}
                  {undated.length > 0 && (
                    <ul className="space-y-1 text-sm text-slate-600">
                      {undated.map((ev, i) => eventLine(ev, i))}
                    </ul>
                  )}
                </div>
              ) : (
                <ul className="space-y-1 text-sm text-slate-600">
                  {events.map((ev, i) => eventLine(ev, i))}
                </ul>
              )}
              <div className="flex gap-3">
                {cardButton(card.approve_label || '승인', 'primary', () => approveCalendar(card), 'approve')}
                {card.change_label && cardButton(card.change_label, 'secondary', changeCard, 'change')}
              </div>
            </div>
          </div>
        );
      }
      case 'FormCard':
        return (
          <div className={cardWrap}>
            <div className={cardBody}>
              {cardHeader(card.title, card.subtitle)}
              <div className="space-y-3">{(card.fields || []).map(renderFormField)}</div>
              {cardButton(
                card.submit?.label || '제출',
                'primary',
                () => submitSummary(serializeForm(card)),
                'submit',
              )}
            </div>
          </div>
        );
      case 'AskUserQuestionCard': {
        // Deliberately NOT wrapped in `cardWrap`: this surface renders inline in
        // the message flow (chip + question + option buttons), not as a bordered
        // card like the others. Options show their `description` so the user
        // picks between consequences rather than bare labels.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic question
        const questions: any[] = Array.isArray(card.questions) ? card.questions : [];
        return (
          <div className="space-y-4">
            {questions.map((q, qi: number) => {
              const multi = q.multiSelect === true;
              const picked = answers[qi] || [];
              return (
                <div key={qi} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                      {q.header}
                    </span>
                    {multi && <span className="text-xs text-slate-400">복수 선택</span>}
                  </div>
                  <div className="text-sm font-medium text-slate-800">{q.question}</div>
                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI dynamic option */}
                    {(q.options || []).map((opt: any, oi: number) => {
                      const active = picked.includes(opt.label);
                      return (
                        <button
                          key={oi}
                          disabled={disabled}
                          onClick={() => toggleAnswer(qi, opt.label, multi)}
                          className={`text-left px-3 py-2 rounded-lg border transition-colors disabled:cursor-not-allowed ${
                            active
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 bg-white hover:bg-slate-50 disabled:bg-slate-50'
                          }`}
                        >
                          <div
                            className={`text-sm font-medium ${active ? 'text-blue-700' : 'text-slate-800'}`}
                          >
                            {opt.label}
                          </div>
                          {opt.description && (
                            <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>
                          )}
                        </button>
                      );
                    })}
                    {/* "Other" is always client-rendered — never in the card schema. */}
                    <input
                      type="text"
                      value={otherText[qi] || ''}
                      onChange={(e) =>
                        setOtherText((prev) => ({ ...prev, [qi]: e.target.value }))
                      }
                      disabled={disabled}
                      placeholder="보기에 없다면 직접 입력하세요"
                      className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
                    />
                  </div>
                </div>
              );
            })}
            <button
              disabled={disabled || !allQuestionsAnswered(card)}
              onClick={() => submitSummary(serializeQuestions(card))}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              답변 보내기
            </button>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const submittedBadge = submitted && (
    <div className="flex items-center gap-2 text-green-600 text-sm font-medium mt-2 px-4 pb-3">
      <Check size={16} />
      답변이 제출되었습니다
    </div>
  );

  // run-report dispatch: the analysis catalog has its own component set and data
  // model, and taps become `surface_action` instead of a summarised text reply.
  if (surface.createSurface?.catalogId === CATALOG_ID_ANALYSIS) {
    return (
      <div className="mt-3">
        <RunReportSurface
          surfaceId={surface.createSurface.surfaceId}
          components={components}
          dataModel={surface.createSurface.dataModel}
          isInteractive={isInteractive}
          onSurfaceAction={onSurfaceAction}
        />
      </div>
    );
  }

  // place-search dispatch: a single PlacesCard root → dedicated renderer
  // (own hooks: SDK loading, render-time open_now clock). Non-interactive display
  // surface, so it doesn't use the submit/dataModel plumbing above.
  const placeRoot = components.find((c) => c.component === 'PlacesCard');
  if (placeRoot) {
    return (
      <div className="mt-3">
        <PlacesCardSurface comp={placeRoot} />
      </div>
    );
  }

  // v1.0 dispatch: a single semantic card root → dedicated card renderer.
  // Anything else falls through to the legacy v0.9 primitive tree below so
  // pre-migration persisted surfaces (and forward-compat unknowns) still render.
  const rootCard = components.find((c) => CARD_TYPES.has(c.component));
  if (rootCard) {
    return (
      <div className="mt-3">
        {renderCard(rootCard)}
        {submittedBadge}
      </div>
    );
  }

  // Find root components (not referenced as child by any other component)
  const referencedIds = new Set<string>();
  components.forEach((c) => {
    if (c.child) referencedIds.add(c.child);
    if (c.children) (c.children as string[]).forEach((id) => referencedIds.add(id));
  });
  const roots = components.filter((c) => !referencedIds.has(c.id));

  return (
    <div className="mt-3">
      {roots.map((root) => renderComponent(root))}
      {submittedBadge}
    </div>
  );
});
A2UISurfaceRenderer.displayName = 'A2UISurfaceRenderer';

// ── Shared chat components ──

export const computeThreadStats = (activeMessages: ChatMessage[], archiveTotal: number): ThreadStats => {
  const activeHuman = activeMessages.filter((m) => m.role === 'user');
  const lastMsg = activeMessages[activeMessages.length - 1];
  const idleMs = lastMsg ? Math.max(0, Date.now() - lastMsg.timestamp.getTime()) : 0;
  const estimatedArchiveHumanTurns = Math.ceil(archiveTotal / 2);

  // Get actual token count from last assistant message's Gemini usage metadata
  const lastAssistant = [...activeMessages].reverse().find((m) => m.role === 'assistant');
  const activeTokens =
    lastAssistant?.metadata
      ? (lastAssistant.metadata.input_tokens ?? 0) + (lastAssistant.metadata.output_tokens ?? 0)
      : 0;

  return {
    totalTurns: activeHuman.length + estimatedArchiveHumanTurns,
    activeTurns: activeHuman.length,
    activeTokens,
    idleHours: Math.round((idleMs / (1000 * 60 * 60)) * 100) / 100,
  };
};

export const LoadMoreArchiveButton: React.FC<{
  loading: boolean;
  onClick: () => void;
}> = ({ loading, onClick }) => (
  <div className="text-center py-3">
    <button
      onClick={onClick}
      disabled={loading}
      className="px-4 py-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
    >
      {loading ? <Loader2 className="animate-spin inline mr-1" size={14} /> : null}
      이전 메시지 보기
    </button>
  </div>
);

export const ThreadSummaryCard: React.FC<{
  summary: string;
  expanded: boolean;
  onToggle: () => void;
}> = ({ summary, expanded, onToggle }) => (
  <div className="my-4 bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-amber-100/50 transition-colors"
    >
      <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
        <FileText size={16} />
        대화 요약
      </div>
      {expanded ? (
        <ChevronDown size={16} className="text-amber-500" />
      ) : (
        <ChevronRight size={16} className="text-amber-500" />
      )}
    </button>
    {expanded && (
      <div className="px-4 pb-3 text-sm text-amber-900 leading-relaxed border-t border-amber-200 pt-2">
        <MarkdownContent content={summary} />
      </div>
    )}
  </div>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- message metadata is dynamic
const CacheMetrics: React.FC<{ metadata: Record<string, any> }> = ({ metadata }) => {
  const cacheHitRate = metadata.cache_hit_rate ?? 0;
  const cacheRead = metadata.cache_read_tokens ?? 0;
  const inputTokens = metadata.input_tokens ?? 0;
  const outputTokens = metadata.output_tokens ?? 0;
  const generatingEmitted: number | undefined = metadata.generating_emitted;
  const promptLabel: string | undefined = metadata.prompt_label;

  const rateColor = cacheHitRate >= 70 ? 'text-emerald-600' : cacheHitRate >= 30 ? 'text-amber-600' : 'text-red-500';

  return (
    <div className="flex items-center gap-3 text-xs text-slate-400 mt-1 ml-14 flex-wrap">
      <span className={`flex items-center gap-1 font-medium ${rateColor}`}>
        <Zap size={11} />
        Cache {cacheHitRate}%
      </span>
      <span>
        {cacheRead.toLocaleString()} / {inputTokens.toLocaleString()} input
      </span>
      <span>{outputTokens.toLocaleString()} output</span>
      {/* generating_emitted — hidden behind a short `gen:N` label. HTML title tooltip
          is required because the abbreviation is meaningless to new users (§7.3). */}
      {typeof generatingEmitted === 'number' && generatingEmitted > 0 && (
        <span
          title="tool_call(generating) 이벤트 발행 횟수 — 2-phase UX (spec §7.3)"
          className="font-mono"
        >
          gen:{generatingEmitted}
        </span>
      )}
      {promptLabel && promptLabel !== 'production' && (
        <span
          title="요청 시 사용한 Langfuse 프롬프트 레이블"
          className="font-mono text-indigo-500"
        >
          label:{promptLabel}
        </span>
      )}
    </div>
  );
};

export const ChatMessageBubble: React.FC<{
  msg: ChatMessage;
  children?: React.ReactNode;
  className?: string;
  a2uiElement?: React.ReactNode;
}> = React.memo(({ msg, children, className, a2uiElement }) => (
  <div className={className || ''}>
    <div className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          msg.role === 'user' ? 'bg-blue-100 text-blue-600' : 'bg-indigo-100 text-indigo-600'
        }`}
      >
        {msg.role === 'user' ? <UserIcon size={20} /> : <Bot size={20} />}
      </div>
      <div
        className={`max-w-[80%] p-4 rounded-2xl ${
          msg.role === 'user'
            ? 'bg-blue-600 text-white rounded-tr-none'
            : 'bg-white border border-slate-200 shadow-sm rounded-tl-none'
        }`}
      >
        {children || (
          <div className="leading-relaxed prose-sm max-w-none">
            <MarkdownContent content={msg.content} isUser={msg.role === 'user'} />
          </div>
        )}
        {a2uiElement}
      </div>
    </div>
    {msg.role === 'assistant' && msg.metadata?.cache_hit_rate !== undefined && <CacheMetrics metadata={msg.metadata} />}
  </div>
));
ChatMessageBubble.displayName = 'ChatMessageBubble';

export const isFirstActiveMessage = (messages: ChatMessage[], msgIdx: number): boolean => {
  const msg = messages[msgIdx];
  if (msg.source !== 'active') return false;
  if (msgIdx === 0) return true;
  return messages[msgIdx - 1]?.source === 'archive';
};
